import { useEffect, useRef, useState } from 'react';

const benefits = [
  {
    icon: (
      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
      </svg>
    ),
    title: 'Watch on the Big Screen',
    description: 'Trail entries arrive on your TV. No scrolling on your phone — settle into the couch, pick up the remote, and receive guidance with presence and intention.'
  },
  {
    icon: (
      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
      </svg>
    ),
    title: 'They Say Your Name',
    description: 'This isn\'t generic content. Your guide records specifically for you. They say your name. They reference your situation. It\'s a letter, not a broadcast.'
  },
  {
    icon: (
      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
    ),
    title: 'Feel Held Between Sessions',
    description: 'Calls are powerful, but they end. Trails fill the space between. Open your TV and there it is — a reminder that someone is walking alongside you.'
  },
  {
    icon: (
      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>
    ),
    title: 'A Record of Your Journey',
    description: 'Every trail entry is saved. Over time, you build a video archive of personal guidance — a record of your growth you can revisit anytime.'
  }
];

export default function ForSubscribers() {
  const [visibleItems, setVisibleItems] = useState(new Array(benefits.length).fill(false));
  const itemRefs = useRef([]);

  useEffect(() => {
    const observers = itemRefs.current.map((ref, index) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            setVisibleItems(prev => {
              const newVisible = [...prev];
              newVisible[index] = true;
              return newVisible;
            });
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(ref);
      return observer;
    });
    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <section className="py-20 px-6 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-cta text-white px-4 py-2 rounded-full text-sm font-montserrat font-semibold mb-4">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
            </svg>
            For Subscribers
          </div>
          <h2 className="text-4xl md:text-5xl text-dark-teal mb-4">
            Receive Guidance on TV
          </h2>
          <p className="text-xl text-secondary-text font-lato max-w-3xl mx-auto">
            Subscribe to a guide. Receive personal video journals on your TV. 
            Lean back and let guidance come to you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={`bg-white rounded-boggler p-6 shadow-card hover:shadow-boggler transition-all duration-300 flex items-start gap-4 scroll-animate ${visibleItems[index] ? 'visible' : ''}`}
            >
              <div className="text-orange-cta flex-shrink-0">
                {benefit.icon}
              </div>
              <div>
                <h3 className="text-xl text-heading-dark mb-2 font-montserrat font-semibold">
                  {benefit.title}
                </h3>
                <p className="text-primary-text font-lato leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-warm-cream rounded-boggler p-8 max-w-2xl mx-auto text-center">
          <p className="text-lg text-primary-text font-lato italic mb-4">
            "There's something different about watching my guide on the TV instead of my phone. 
            It feels more... intentional. Like I'm really sitting down to receive something."
          </p>
          <p className="text-sm text-muted-text font-montserrat">The experience we're building toward</p>
        </div>
      </div>
    </section>
  );
}
